import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Spin } from 'antd';
import { useAppSelector, useAppDispatch } from '../../hooks/redux';
import { updateSubscriptionInfo } from '../../store/slices/authSlice';
import { useGetSubscriptionStatusQuery } from '../../services/stripeApi';
import { useGetUserLimitInfoQuery } from '../../services/usersApi';
import { getDefaultRouteForRole } from '../../utils/roleHelpers';

interface AuthenticatedRedirectProps {
  /** Optional plan from URL to pass to select-plan */
  planFromUrl?: string | null;
}

/**
 * AuthenticatedRedirect - Handles post-login redirection based on real Stripe subscription status
 *
 * This component fetches the actual subscription status from Stripe before deciding
 * where to redirect the user. This ensures that even if webhooks haven't updated
 * the database, the user is redirected correctly based on their actual subscription.
 */
const AuthenticatedRedirect: React.FC<AuthenticatedRedirectProps> = ({ planFromUrl }) => {
  const dispatch = useAppDispatch();
  const {
    user,
    plan: reduxPlan,
    isTrialExpired: reduxIsTrialExpired,
    trialEndDate: reduxTrialEndDate,
  } = useAppSelector(state => state.auth);
  const [redirectPath, setRedirectPath] = useState<string | null>(null);

  // Fetch subscription status from Stripe
  const { data: subscriptionStatus, isLoading: loadingSubscription, isError: errorSubscription } = useGetSubscriptionStatusQuery();

  // Fetch user limit info
  const { data: limitInfo, isLoading: loadingLimitInfo, isError: errorLimitInfo } = useGetUserLimitInfoQuery();

  useEffect(() => {
    // Wait for both queries to complete
    if (loadingSubscription || loadingLimitInfo) return;

    // If there's an error fetching Stripe data, fall back to Redux state
    if (errorSubscription || errorLimitInfo || !subscriptionStatus || !limitInfo) {
      // Check if plan is not trial or expired (any other plan is considered active)
      const hasActivePlan = reduxPlan && reduxPlan !== 'trial' && reduxPlan !== 'expired';

      if (hasActivePlan) {
        setRedirectPath(getDefaultRouteForRole(user?.role));
      } else {
        // Check if trial is still valid. Si la consulta falló, la fecha del login (Redux)
        // manda: antes se leía solo de limitInfo y, sin él, a un empleado en una prueba
        // vigente se le mandaba a elegir plan en cada inicio de sesión.
        const trialEndDate = limitInfo?.trialEndDate ?? reduxTrialEndDate;
        const isTrialValid = trialEndDate && new Date(trialEndDate) > new Date();
        if (isTrialValid) {
          setRedirectPath(getDefaultRouteForRole(user?.role));
        } else {
          const target = planFromUrl ? `/select-plan?plan=${encodeURIComponent(planFromUrl)}` : '/select-plan';
          setRedirectPath(target);
        }
      }
      return;
    }

    // Use Stripe data as source of truth
    const stripePlanName = subscriptionStatus.planName;
    const stripeStatus = subscriptionStatus.status;
    const hasSubscription = subscriptionStatus.hasSubscription;
    const isStripeActive = stripeStatus === 'active' || stripeStatus === 'trialing';
    const hasSubscriptionWithIssues = hasSubscription && !isStripeActive; // past_due, unpaid, canceled, etc.

    // Check if trial is still valid (from database)
    const trialEndDate = subscriptionStatus.trialEnd || limitInfo.trialEndDate;
    const isTrialValid = trialEndDate && new Date(trialEndDate) > new Date();

    // Update Redux with the real subscription status
    const isTrialExpiredFromStripe = !isStripeActive && !hasSubscription && !isTrialValid;
    if (reduxPlan !== stripePlanName || reduxIsTrialExpired !== isTrialExpiredFromStripe) {
      dispatch(updateSubscriptionInfo({
        plan: stripePlanName,
        isTrialExpired: isTrialExpiredFromStripe,
        maxUsers: limitInfo.maxUsers,
        trialEndDate: trialEndDate,
      }));
    }

    // Decide where to redirect
    if (isStripeActive || isTrialValid) {
      // User has an active subscription or valid trial - go to dashboard
      setRedirectPath(getDefaultRouteForRole(user?.role));
    } else if (hasSubscriptionWithIssues) {
      // User has a subscription but with issues (past_due, unpaid, canceled) - go to subscription management
      setRedirectPath('/subscription');
    } else {
      // User doesn't have any subscription and trial expired - go to select-plan
      const target = planFromUrl ? `/select-plan?plan=${encodeURIComponent(planFromUrl)}` : '/select-plan';
      setRedirectPath(target);
    }
  }, [
    loadingSubscription,
    loadingLimitInfo,
    errorSubscription,
    errorLimitInfo,
    subscriptionStatus,
    limitInfo,
    reduxPlan,
    reduxIsTrialExpired,
    reduxTrialEndDate,
    user?.role,
    planFromUrl,
    dispatch,
  ]);

  // Show loading while checking subscription
  if (!redirectPath) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        flexDirection: 'column',
        gap: '16px',
        background: 'linear-gradient(135deg, #f0fdfc 0%, #e0f7f6 100%)',
      }}>
        <Spin size="large" />
        <div style={{ color: 'var(--text-secondary)' }}>Verificando tu suscripcion...</div>
      </div>
    );
  }

  return <Navigate to={redirectPath} replace />;
};

export default AuthenticatedRedirect;
